import React from "react";
import { FiBarChart2, FiVideo } from "react-icons/fi";
import { GetStartedButton } from "../../components/common/GetStartedButton";
import CoachView1 from "../../assets/images/CoachView1.jpeg";
import CoachView2 from "../../assets/images/CoachView2.jpeg";
import CoachView3 from "../../assets/images/CoachView3.jpeg";

const SCREENS = [
  { title: "ROSTERS AVAILABLE FOR EVERY TEAM", image: CoachView1, alt: "JUCO Pipeline team rosters screen" },
  { title: "ADVANCED SORTING AND STATISTICS", image: CoachView2, alt: "JUCO Pipeline sortable statistics screen" },
  { title: "FOLLOW YOUR PROSPECTS", image: CoachView3, alt: "JUCO Pipeline followed players screen" },
];

const COVERAGE = [
  { value: "500+", label: "JUCO Baseball Programs" },
  { value: "35", label: "States Covered" },
  { value: "NJCAA + 3C2A", label: "Both Major JUCO Associations" },
];

const EVALUATION_TOOLS = [
  {
    title: "Advanced Statistics",
    description:
      "Go beyond the box score. Sort and compare players with advanced metrics to see who really produces.",
    Icon: FiBarChart2,
  },
  {
    title: "Player Video",
    description:
      "See the swing, the arm, and the athleticism for yourself. Watch video alongside the numbers before you pick up the phone.",
    Icon: FiVideo,
  },
];

const ForCoaches = () => {
  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Header band: navy in both themes so the white navbar stays readable */}
      <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-8 lg:gap-12 lg:items-end">
          <div>
            <p className="text-yellow-300 text-sm sm:text-base font-bold uppercase tracking-widest mb-4">
              For Coaches
            </p>
            <h1 className="text-5xl sm:text-6xl xl:text-7xl font-anton leading-[1.1] tracking-tight text-white">
              RECRUITING SHOULDN’T FEEL
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
                LIKE GUESSWORK
              </span>
            </h1>
          </div>
          <div>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-3">
              With JUCO Pipeline, you get access to a powerful scouting system designed specifically for
              2-year college athletes, with advanced statistics and video to help you evaluate every player.
            </p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-6">
              Built for NCAA, NAIA, and other four-year programs — plus summer collegiate leagues.
            </p>
            <GetStartedButton>Find JUCO Players</GetStartedButton>
          </div>
        </div>
      </section>

      {/* Coverage stats */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-8 max-w-3xl">
            Data on more than 500 junior college baseball programs across 35 states, covering the{" "}
            <span className="text-cyan-500">NJCAA</span> and the <span className="text-cyan-500">3C2A</span>.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {COVERAGE.map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 px-6 py-6"
              >
                <p className="text-5xl sm:text-6xl font-anton leading-none tracking-tight bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
                  {stat.value}
                </p>
                <p className="mt-3 text-jp-ink font-bold uppercase tracking-wider text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Evaluation tools */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto">
          <p className="text-cyan-500 text-sm font-bold uppercase tracking-widest mb-3">
            Evaluate Players with Confidence
          </p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-8 max-w-3xl">
            Advanced statistics and video, together in one place.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {EVALUATION_TOOLS.map(({ title, description, Icon }) => (
              <div
                key={title}
                className="rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 px-6 py-6 flex gap-5"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-jp-band flex items-center justify-center">
                  <Icon className="w-6 h-6 text-cyan-400" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="text-jp-ink font-bold uppercase tracking-wider text-base mb-2">{title}</h3>
                  <p className="text-jp-ink/80 text-sm sm:text-base leading-relaxed">{description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product screens */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {SCREENS.map((screen) => (
            <figure key={screen.title} className="m-0">
              <div className="rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-jp-line bg-jp-section">
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                </div>
                <div className="aspect-[16/10] bg-[#0B1220]">
                  <img src={screen.image} alt={screen.alt} className="w-full h-full object-contain" />
                </div>
              </div>
              <figcaption className="mt-3 text-jp-ink font-bold uppercase tracking-wider text-sm">
                {screen.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Closing call to action */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto rounded-2xl bg-jp-band border border-jp-outline/70 dark:border-jp-gold/40 px-6 py-8 sm:px-10 sm:py-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <p className="text-yellow-300 text-xs font-bold uppercase tracking-widest mb-2">
              From Roster Gaps to Future Stars
            </p>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white">
              Find exactly what you’re looking for, <span className="text-cyan-400">without the noise.</span>
            </h2>
          </div>
          <div className="flex-shrink-0">
            <GetStartedButton>Find JUCO Players</GetStartedButton>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ForCoaches;
